import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';

/**
 * «Invita a alguien» — el panel del miembro (T12B · objetivo 3).
 *
 * Vive dentro de D7 (`/n8n-chile`), que es la página del grupo: quien ya está dentro entra ahí a
 * ver de qué va y se encuentra con que puede traer gente. Un sitio propio obligaría a descubrir
 * una ruta más.
 *
 * SE MONTA SIEMPRE Y DECIDE ÉL: el servidor manda `canInvite` y, si dice que no, el `reason`.
 * Así el mismo componente sirve para el miembro (formulario), para quien no lo es (nada) y para
 * quien no ha confirmado el correo (el motivo, que tiene arreglo). Decidirlo en el padre
 * repartiría la política entre dos ficheros.
 *
 * OJO: `flarum/common/Component`, NO `.../components/Component` — ver la nota de T12.
 */
type Redemption = { at: string | null; name: string | null };

type InviteRow = {
  id: number;
  maxUses: number;
  uses: number;
  usable: boolean;
  reason: string | null;
  createdAt: string | null;
  expiresAt: string | null;
  redemptions: Redemption[];
};

type IssuedInvite = { id: number; url: string };

export default class InvitarPanel extends Component {
  loaded = false;
  canInvite = false;
  reason: string | null = null;
  invites: InviteRow[] = [];
  creating = false;
  error: string | null = null;
  copied: number | null = null;
  issued: IssuedInvite | null = null;

  oninit(vnode: any) {
    super.oninit(vnode);

    // Sin sesión no hay nada que pedir: el endpoint respondería 401 y saldría una alerta roja
    // en una página pública que por lo demás funciona.
    if (app.session.user) this.load();
  }

  view() {
    if (!app.session.user || !this.loaded) return null;

    // No es miembro (ni admin): esta sección no le corresponde. La página ya le ofrece
    // solicitar acceso; añadir aquí un «no puedes invitar» sería decirle que no a algo que no
    // ha pedido.
    if (!this.canInvite && this.reason !== 'unconfirmed') return null;

    return (
      <section className="CmInvitar" data-cm-invitar={this.canInvite ? 'ready' : this.reason}>
        <h2 className="CmInvitar-title">{this.t('title')}</h2>
        <p className="CmInvitar-sub">{this.t(this.canInvite ? 'body' : 'reason_' + this.reason)}</p>

        {this.canInvite ? this.form() : null}
        {this.canInvite ? this.issuedLink() : null}
        {this.canInvite ? this.list() : null}
      </section>
    );
  }

  issuedLink() {
    if (!this.issued) return null;

    return (
      <div className="CmInvitar-item" data-cm-issued-invite={this.issued.id}>
        <b>{this.t('issued_title')}</b>
        <p className="CmInvitar-meta">{this.t('issued_body')}</p>
        <div className="CmInvitar-linkRow">
          <input className="FormControl CmInvitar-link" type="text" readOnly value={this.issued.url} onclick={(e: any) => e.target.select()} />
          <Button className="Button CmInvitar-copy" onclick={(e: any) => this.copy(this.issued!.url, this.issued!.id, e)}>
            {this.copied === this.issued.id ? this.t('copied') : this.t('copy')}
          </Button>
          <Button className="Button Button--link" onclick={() => (this.issued = null)}>
            {this.t('issued_dismiss')}
          </Button>
        </div>
      </div>
    );
  }

  form() {
    const active = this.invites.filter((i) => i.usable).length;

    return (
      <div className="CmInvitar-actions">
        <Button
          className="Button Button--primary CmInvitar-create"
          data-cm-create-invite="1"
          loading={this.creating}
          disabled={this.creating}
          onclick={() => this.create()}
        >
          {this.t('create')}
        </Button>
        <span className="CmInvitar-quota">{this.t('quota', { active, max: this.maxActive })}</span>
        {this.error ? <p className="CmInvitar-error">{this.t('error_' + this.error)}</p> : null}
      </div>
    );
  }

  list() {
    if (!this.invites.length) {
      return <p className="CmInvitar-empty">{this.t('empty')}</p>;
    }

    return (
      <ul className="CmInvitar-list">
        {this.invites.map((invite) => (
          <li className="CmInvitar-item" data-cm-invite={invite.id} data-cm-usable={invite.usable ? '1' : '0'}>
            <div className="CmInvitar-linkRow">
              <span className="CmInvitar-meta">{this.t('link_not_recoverable')}</span>
              {/* Retirar sólo tiene sentido mientras el enlace sirve: uno agotado o caducado ya
                  no da acceso, y ofrecer «retirar» ahí sugeriría que aún hacía algo. */}
              {invite.usable ? (
                <Button className="Button Button--link CmInvitar-revoke" data-cm-revoke={invite.id} onclick={() => this.revoke(invite)}>
                  {this.t('revoke')}
                </Button>
              ) : null}
            </div>

            <p className="CmInvitar-meta">
              {invite.usable ? this.t('meta_usable', { uses: invite.uses, max: invite.maxUses }) : this.t('meta_' + (invite.reason || 'exhausted'))}
              {/*
                NO SE USA `humanTime()` AQUÍ, Y NO ES UN DESCUIDO.

                El helper del core **colapsa cualquier fecha futura a «ahora»** —está pensado
                para fechas de publicación, que siempre son pasadas—, así que una caducidad a 30
                días salía como «caduca hace unos segundos». Medido en el banco:
                `humanTime('2026-09-05…')` renderiza literalmente «hace unos segundos».

                Para una caducidad la fecha absoluta es además la información útil: «caduca el 5
                sept 2026» se puede apuntar en la agenda, «caduca en un mes» no. Se formatea con
                el locale del navegador y el `<time>` lleva el valor exacto en `datetime`.
              */}
              {invite.expiresAt
                ? [' · ', this.t('meta_expires_label'), ' ', <time datetime={invite.expiresAt}>{this.formatDate(invite.expiresAt)}</time>]
                : null}
            </p>

            {/* LA ATRIBUCIÓN, que es el objetivo entero: quién entró por este enlace. */}
            {invite.redemptions.length ? (
              <p className="CmInvitar-who" data-cm-redemptions={invite.redemptions.length}>
                {this.t('who')} {invite.redemptions.map((r) => r.name || this.t('who_deleted')).join(' · ')}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    );
  }

  copy(value: string, id: number, e: any) {
    e.preventDefault();

    const done = () => {
      this.copied = id;
      m.redraw();

      // Y se limpia. Sin esto el botón se queda en «Copiado» para siempre: quien quiera mandar
      // el MISMO enlace a una segunda persona pulsa y no ve ninguna señal de que pasó algo. Al
      // copiar otro enlace, además, la etiqueta saltaba de fila.
      setTimeout(() => {
        if (this.copied === id) {
          this.copied = null;
          m.redraw();
        }
      }, 2000);
    };

    // `navigator.clipboard` no existe fuera de contextos seguros (http sin TLS) y puede estar
    // denegado por permiso: si falla, el input de al lado sigue siendo la vía manual, así que
    // no se pinta ningún error.
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(done, () => {});
      return;
    }

    done();
  }

  // --- datos ----------------------------------------------------------------------------

  load() {
    app
      .request<any>({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/umbral/invites',
        errorHandler: () => {
          this.loaded = true;
          this.canInvite = false;
          m.redraw();
        },
      })
      .then(
        (result: any) => {
          this.loaded = true;
          this.canInvite = !!result.canInvite;
          this.reason = result.reason || null;
          this.invites = result.invites || [];
          this.maxActive = result.maxActive || this.maxActive;
          m.redraw();
        },
        () => {}
      );
  }

  create() {
    this.creating = true;
    this.error = null;
    m.redraw();

    app
      .request<any>({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/umbral/invites',
        body: { data: { attributes: {} } },
        errorHandler: (e: any) => {
          this.creating = false;
          const raw = String(e?.response?.errors?.[0]?.code || '');
          const code = raw === 'umbral_rate_limited' ? 'rate_limited' : raw.replace(/^umbral_invite_/, '');
          this.error = ['too_many', 'forbidden', 'not_configured', 'rate_limited'].includes(code) ? code : 'generic';
          m.redraw();
        },
      })
      .then(
        (result: any) => {
          this.creating = false;
          // Delante: es lo que la persona acaba de crear y lo que va a copiar.
          if (result && result.invite) {
            const issued = result.invite;
            this.issued = { id: issued.id, url: String(issued.url || '') };
            const { token: _token, url: _url, ...summary } = issued;
            this.invites.unshift(summary as InviteRow);
          }
          m.redraw();
        },
        () => {}
      );
  }

  /**
   * Retira un enlace.
   *
   * Se pide confirmación porque es irreversible —no hay «des-revocar»— y el enlace ya puede
   * estar en manos de alguien: retirarlo de un clic accidental deja a esa persona con una
   * invitación muerta y sin saber por qué.
   */
  revoke(invite: InviteRow) {
    if (!confirm(String(this.t('revoke_confirm')))) return;

    app
      .request<any>({
        method: 'DELETE',
        url: app.forum.attribute('apiUrl') + '/umbral/invites/' + invite.id,
        errorHandler: () => {
          this.error = 'generic';
          m.redraw();
        },
      })
      .then(
        () => {
          // Se marca en local en vez de recargar la lista entera: el resto de filas no ha
          // cambiado y una recarga haría parpadear toda la sección.
          invite.usable = false;
          invite.reason = 'revoked';
          m.redraw();
        },
        () => {}
      );
  }

  maxActive = 10;

  /**
   * Fecha corta en el idioma del navegador («5 sept 2026»).
   *
   * `undefined` como locale deja elegir al navegador en vez de fijar `es`: el foro se sirve en
   * español pero la fecha la lee una persona con su propia configuración, y el mes abreviado es
   * de las pocas cosas que conviene que salgan en su idioma.
   */
  formatDate(iso: string): string {
    const date = new Date(iso);

    if (isNaN(date.getTime())) return '';

    try {
      return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      // `toLocaleDateString` puede lanzar con opciones no soportadas en runtimes viejos.
      return date.toISOString().slice(0, 10);
    }
  }

  t(key: string, params?: any) {
    return app.translator.trans('umbral-comunidad.forum.invitar.' + key, params);
  }
}
